import { createFileRoute, Link } from "@tanstack/react-router";
import { ServicePageLayout } from "@/components/service/ServicePageLayout";
import { serviceHead } from "@/lib/service-head";
import { loadPageSettings } from "@/lib/page-seo-loader";

const SLUG = "/apartment-roofing";
const NAME = "Apartment & Condo Roofing";
const TITLE = "Apartment & Condo Roofing in Phoenix | Durafoam Roofing";
const DESCRIPTION =
  "Phoenix apartment and condo roofing — multi-unit re-roofs, leak response, and maintenance for property owners and management companies. Family-owned since 1989.";

const faqs = [
  { q: "Do you re-roof occupied apartment buildings?", a: "Yes — almost all multifamily re-roofs we do are on occupied buildings. We sequence the work to minimize disruption and coordinate notices with property management." },
  { q: "How do you handle tenant complaints during work?", a: "Pre-work notices, a dedicated point of contact for the property manager, and a same-day callback policy for any tenant concern that comes up during construction." },
  { q: "What roof systems are best for apartment buildings?", a: "Most multifamily roofs in Phoenix are flat or low-slope and benefit from spray foam — it insulates well, is seamless around the many penetrations a multifamily roof has, and reduces cooling costs across every unit underneath." },
  { q: "Can you coordinate with property management software?", a: "We send digital reports, photos, and invoices that fit easily into AppFolio, Yardi, or Buildium workflows. We've worked with most Valley property management firms at one point or another." },
  { q: "Do you respond to emergency leaks at apartments?", a: "Yes. Apartments are a priority because a single leak can damage multiple units. Same-day or next-day response during monsoon season." },
];

export const Route = createFileRoute("/apartment-roofing")({
  loader: () => loadPageSettings(SLUG),
  head: ({ loaderData }) => serviceHead({ override: loaderData?.seo, slug: SLUG, title: TITLE, description: DESCRIPTION, serviceName: NAME, faqs, rating: loaderData?.rating, reviewCount: loaderData?.reviewCount }),
  component: Page,
});

function Page() {
  return (
    <ServicePageLayout
      pageSlug="apartment-roofing"
      eyebrow="Multifamily"
      h1="Apartment & Condo Roofing in Phoenix"
      subhead="Multifamily roofing for owners and property management companies — from emergency leak response to phased re-roofs that don't disrupt your tenants."
      heroBullets={["Occupied-building re-roofs", "Multi-unit leak triage", "Foam, TPO, and mod-bit systems", "PM-friendly reporting and invoicing"]}
      whatIs={{
        heading: "Apartment and condo roofing — what's different",
        body: (
          <>
            <p>
              Multifamily roofing combines commercial complexity with residential sensitivity. You
              have tenants underneath, a manager coordinating, an owner expecting predictable
              costs, and often a portfolio of buildings of varying ages and roof types. The roofer
              who can handle all of that without disrupting the property is hard to find.
            </p>
            <p>
              We work with apartment owners, condo HOAs, and property management firms across the
              Valley. The work ranges from single-unit leak response to full multi-building
              re-roof programs. Every job comes with the same expectations: clean scheduling,
              clear communication, and documentation that fits cleanly into your property
              management workflow.
            </p>
            <p>
              The best multifamily roof in Phoenix is usually spray polyurethane foam. It
              insulates well across every unit, seals seamlessly around the many penetrations a
              multifamily roof has, and reduces cooling load on building-wide HVAC. For how the
              system works on larger buildings — including foam-over reroofs on aging TPO and
              modified bitumen — see{" "}
              <Link
                to="/commercial-foam-roofing"
                className="font-semibold text-primary underline underline-offset-2 hover:text-accent"
              >
                commercial foam roofing
              </Link>
              .
            </p>
          </>
        ),
      }}
      climate={{
        heading: "Phoenix multifamily roof realities",
        body: (
          <>
            <p>
              Multifamily roofs deal with the same Phoenix climate as single-family roofs, but
              with operational complexity that single-family roofs don't have.
            </p>
            <ul>
              <li><strong>One leak, many units:</strong> Water travels along the deck before dripping. A single failure can damage multiple units stacked below. Fast diagnosis matters.</li>
              <li><strong>HVAC density:</strong> Multifamily roofs are covered in equipment. Every curb and conduit is a potential leak point. Foam wraps these continuously.</li>
              <li><strong>Energy load:</strong> Cooling costs are owner-paid in many configurations. Reflective roofs cut monthly utility burn measurably.</li>
              <li><strong>Tenant impact:</strong> Construction noise, parking access, and trash management all matter when you're working over occupied units.</li>
            </ul>
          </>
        ),
      }}
      process={{
        heading: "Our multifamily roofing process",
        steps: [
          { title: "Walk the property", body: "Inspect every roof, document conditions, and align with management on scope, sequencing, and tenant communication." },
          { title: "Phased scope and proposal", body: "Written proposal with system, phasing, schedule, warranty, and tenant notice plan. Multiple options when appropriate." },
          { title: "Coordinated install", body: "Crews coordinate with property staff. Daily progress updates, contingency for weather, and clean staging that respects tenant traffic." },
          { title: "Ongoing service", body: "Optional recurring maintenance and on-call leak response so future issues are handled before they damage units." },
        ],
      }}
      faqs={faqs}
      ctaTitle="Need a multifamily roofer who actually shows up?"
      ctaBody="Call us or request an inspection. We'll meet you on-site, walk the property, and lay out a plan that fits your buildings and your budget."
    />
  );
}
